import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Trophy } from "lucide-react";

const LANDING_LINKS = [
  { label: "STORIA", to: "/chi-siamo" },
  { label: "LAVORA CON NOI", to: "/lavora-con-noi" },
  { label: "CONTATTI", to: "/contatti" },
];
const AUTO_LINKS = [
  { label: "SERVIZI", to: "/auto/servizi" },
  { label: "OFFICINA", to: "/auto/officina" },
  { label: "NOLEGGIO", to: "/auto/noleggio" },
  { label: "AUTOSCOUT24", to: "https://www.autoscout24.it/concessionari/nsa-srl", external: true },
];
const MOTO_LINKS = [
  { label: "KTM", to: "/moto/ktm", brand: "ktm", logo: "/logos/ktm.png" },
  { label: "HUSQVARNA", to: "/moto/husqvarna", brand: "husqvarna", logo: "/logos/husqvarna.png" },
  { label: "GASGAS TRIAL", to: "/moto/gasgas", brand: "gasgas", logo: "/logos/gasgas.png" },
  { label: "RACING TEAM", to: "/moto/racing-team" },
  { label: "EVENTI", to: "/moto/eventi" },
  { label: "CATALOGO", to: "https://dealer.moto.it/nsaservizi/Nuovo", external: true },
];

const BRAND_COLORS = {
  ktm: "#FF6600",
  husqvarna: "#1e3a8a",
  gasgas: "#dc2626",
};

function pickLinks(pathname) {
  if (pathname.startsWith("/auto")) return { links: AUTO_LINKS, world: "auto" };
  if (pathname.startsWith("/moto")) return { links: MOTO_LINKS, world: "moto" };
  return { links: LANDING_LINKS, world: "landing" };
}

export default function TopNav({ variant = "auto" }) {
  const location = useLocation();
  const { links, world } = pickLinks(location.pathname);
  const [open, setOpen] = React.useState(false);

  // variant "over-hero" => transparent-dark, "solid" => white glass
  const dark = variant === "over-hero";

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 ${dark ? "glass-nav-dark text-white" : "glass-nav text-gray-900"}`}
      data-testid="top-nav"
    >
      <div className="nsa-container flex items-center justify-between h-16 md:h-20 gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img
            src={dark ? "/logos/nsa-motors-white.png" : "/logos/nsa-motors.png"}
            alt="NSA Motors"
            className="h-8 md:h-11 w-auto object-contain"
          />
        </Link>

        {/* Mobile: brand logos + RACING TEAM inline nella top bar (solo mondo Moto) */}
        {world === "moto" && (
          <div className="md:hidden flex items-center gap-1.5 flex-1 justify-center min-w-0">
            {MOTO_LINKS.filter((l) => l.logo).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                className={({ isActive }) =>
                  `inline-flex items-center justify-center shrink-0 transition-transform active:scale-95 ${
                    isActive ? "ring-2 ring-offset-1 ring-offset-white" : "opacity-95"
                  }`
                }
                data-testid={`mnav-brand-${l.brand}`}
                aria-label={l.label}
              >
                <img src={l.logo} alt={l.label} className="h-10 w-10 object-contain" />
              </NavLink>
            ))}
            <NavLink
              to="/moto/racing-team"
              end
              className={({ isActive }) =>
                `label-eyebrow text-[9px] leading-tight text-center px-1.5 py-1 shrink-0 ml-1 border ${
                  isActive
                    ? "bg-[color:var(--nsa-orange)] text-white border-[color:var(--nsa-orange)]"
                    : dark
                    ? "text-white border-white/40"
                    : "text-gray-800 border-gray-300"
                }`
              }
              data-testid="mnav-brand-racing"
              aria-label="Racing Team"
            >
              RACING<br/>TEAM
            </NavLink>
          </div>
        )}

        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => {
            if (l.external) {
              return (
                <a
                  key={l.to}
                  href={l.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`label-eyebrow link-underline inline-flex items-center gap-1.5 ${
                    dark ? "text-white/90 hover:text-white" : "text-gray-700 hover:text-gray-900"
                  }`}
                  aria-label={l.label}
                  title={l.label}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              );
            }
            return (
            <NavLink
              key={l.to}
              to={l.to}
              end
              style={l.brand ? { "--hover-color": BRAND_COLORS[l.brand] } : undefined}
              className={({ isActive }) =>
                l.logo
                  ? `inline-flex items-center justify-center transition-transform hover:-translate-y-0.5 hover:scale-105 ${isActive ? "ring-2 ring-offset-2 ring-offset-white" : "opacity-90 hover:opacity-100"}`
                  : `label-eyebrow link-underline ${
                      isActive
                        ? "active text-[color:var(--nsa-orange)]"
                        : dark
                        ? "text-white/90 hover:text-white"
                        : "text-gray-700 hover:text-gray-900"
                    }`
              }
              aria-label={l.label}
              title={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.logo ? (
                <img
                  src={l.logo}
                  alt={l.label}
                  className="h-12 w-12 md:h-14 md:w-14 object-contain"
                  style={{ objectFit: "contain" }}
                />
              ) : (
                l.label
              )}
            </NavLink>
          );
          })}
          <Link
            to="/contatti#form"
            className={`label-eyebrow px-5 py-3 transition-colors ${
              dark
                ? "bg-white text-gray-900 hover:bg-white/90"
                : "bg-gray-900 text-white hover:bg-black"
            }`}
            data-testid="nav-scrivici-btn"
          >
            SCRIVICI
          </Link>
        </nav>

        <button
          className={`md:hidden p-2 ${dark ? "text-white" : "text-gray-900"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Apri menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile strip rimossa — loghi già presenti nella top bar */}

      {open && (
        <div className={`md:hidden ${dark ? "bg-black/85 text-white" : "bg-white text-gray-900"} border-t border-black/5`}>
          <div className="nsa-container py-4 flex flex-col gap-4">
            {links
              .filter((l) => !(world === "moto" && (l.logo || l.to === "/moto/racing-team")))
              .map((l) => {
              if (l.external) {
                return (
                  <a
                    key={l.to}
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpen(false)}
                    className="label-eyebrow"
                    data-testid={`mnav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </a>
                );
              }
              return (
              <NavLink
                key={l.to}
                to={l.to}
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `label-eyebrow ${isActive ? "text-[color:var(--nsa-orange)]" : ""}`
                }
                data-testid={`mnav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            );
            })}
            <Link
              to="/contatti#form"
              onClick={() => setOpen(false)}
              className="bg-gray-900 text-white label-eyebrow px-5 py-3 text-center"
              data-testid="mnav-scrivici-btn"
            >
              SCRIVICI
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
}
